import { Component, OnInit, Input, ViewChild, ElementRef, Renderer2, OnChanges, input } from '@angular/core';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { DashboardWorkspaceIdxModel } from 'src/app/core/components/dashboard/models/dashboard.model';
import { Output, EventEmitter } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CloneWorkspacesDialogComponent } from '../clone-workspaces-dialog/clone-workspaces-dialog.component';
import { DashboardApiService } from '../services/dashboard-api.service';
import { WorkspaceCloned } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';

@Component({
    selector: 'app-workspace-tabs',
    templateUrl: './workspace-tabs.component.html',
    styleUrls: ['./workspace-tabs.component.scss'],
    providers: [DialogService],
    standalone: false
})
export class DashboardWorkspaceTabsComponent implements OnInit, OnChanges {

  private _workspaces: DashboardWorkspaceIdxModel[];
  private _selectedTabIndex: number;
  private _organization: string;

  public activeWorkspaceTab:DashboardWorkspaceIdxModel;
  public selectedEditTab: DashboardWorkspaceIdxModel;
  public workspaceOptions: MenuItem[];
  public workspaceContextMenu: MenuItem[];
  public workspaceTabs: MenuItem[];
  public editFromContextMenu: boolean;
  
  ref: DynamicDialogRef | undefined;
  
  handleInput(event: KeyboardEvent): void{
    event.stopPropagation();
  } 
  
  @ViewChild('editNameBox') editNameBox: ElementRef;
  
  @Input()
  public set selectedTabIndex(value: number) {
    if (value) {
      this._selectedTabIndex = value;
    }
  }
  
  public get selectedTabIndex(): number {
    return this._selectedTabIndex;
  }
  
  @Input()
  public set workspaces(value: DashboardWorkspaceIdxModel[]) {
    if (value) {
      this._workspaces = value;
    }
  }
  
  public get workspaces(): DashboardWorkspaceIdxModel[] {
    return this._workspaces;
  }

  public isLoading = input<boolean>(false);
  
  @Output()
  public workspaceInserted = new EventEmitter<{ idx: number }>();
  
  @Output()
  public workspaceDeleted = new EventEmitter<number>();

  @Output()
  public workspaceRenamed  = new EventEmitter<{ idx: number, name: string }>();

  @Output()
  public workspaceSelected = new EventEmitter<number>();

  @Output()
  public workspaceIdx = new EventEmitter<{ fromIdx: number, toIdx: number }>();

  @Output()
  public workspacesCloned = new EventEmitter<any>();

  constructor(
    private renderer: Renderer2,
    public _dialogService: DialogService,
    private _dashboardService: DashboardApiService,
    private _messenger: MediatorService,
    private _storeService: StoreService
  ) {
    this.renderer.listen('window', 'mouseDown', (e: Event) => {
      if (e.target !== this.editNameBox?.nativeElement && !this.editFromContextMenu) {
        this.selectedEditTab = null;
      }
    });
  }

   async ngOnInit() {
    const user = await this._storeService.get<any>(StorageKeys.USER);
    this._organization = user?.profile?.organization || null;

    this.workspaceOptions = [
      {
        id: "add",
        icon: PrimeIcons.PLUS,
        command: () => {
          this.insertWorkspace(this.workspaces.length + 1);
        },
        tooltip: "Add New Workspace"
      },
      {
        id: "clone",
        icon: PrimeIcons.CLONE,
        command: () => {
          this.ref = this._dialogService.open(CloneWorkspacesDialogComponent, {
            header: 'Clone Workspace',
            width: '400px'
          });
          this.ref.onClose.subscribe((dashboardId) => {
            if (dashboardId) {
              this._dashboardService.cloneDashboard(dashboardId, this._organization).subscribe(() => {
                this.workspacesCloned.emit();
              });
            }
          });
        },
        tooltip: "Copy tab and component layout from another design"
      }
    ];
  }

  ngOnChanges() {
    this.activeWorkspaceTab = this.workspaces?.find(wt => wt.index == this._selectedTabIndex);
  }

  public insertWorkspace(idx: number): void {
    this.workspaceInserted.emit({ idx });
    this.selectWorkspace(idx);
  }

  public deleteWorkspace(workspaceIndex: number): void {
    this.workspaceDeleted.emit(workspaceIndex);
    this.selectWorkspace(0);
  }

  public selectWorkspace(workspaceIndex: number): void {
    if (workspaceIndex == 0 || this._selectedTabIndex === workspaceIndex) {
      return;
    }
    this.activeWorkspaceTab = this.workspaces?.find(wt => wt.index == workspaceIndex);
    this.workspaceSelected.emit(workspaceIndex);
  }

  public renameWorkspace(workspaceIndex: string, dashboardName: string) {
    this.workspaceRenamed.emit({ idx: parseInt(workspaceIndex), name: dashboardName });
    this.workspaces.find(ws => ws.index === parseInt(workspaceIndex)).name = dashboardName;
    this.selectedEditTab = null;
  }

  public moveWorkspaceIdx(fromIdx: number, toIdx: number) {
    this.workspaceIdx.emit({ fromIdx: fromIdx, toIdx: toIdx });
  }

  public copyWorkspace() {
    this.ref = this._dialogService.open(CloneWorkspacesDialogComponent, {
      header: 'Clone Workspace',
      width: '400px'
    });

    this.ref.onClose.subscribe((dashboardId) => {
      if (dashboardId) {
        this._dashboardService.cloneDashboard(dashboardId, this._organization).subscribe(() => {
          this.workspacesCloned.emit();
        });
      }
    });
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.workspaces, event.previousIndex, event.currentIndex);
    this.moveWorkspaceIdx(event.previousIndex + 1, event.currentIndex + 1);
  }

  public onContextMenu(e: any, workspace: DashboardWorkspaceIdxModel) {
    this.workspaceContextMenu = [
      {
        label: 'Delete Workspace', icon: PrimeIcons.TRASH, disabled: this.workspaces?.length === 1,
        command: () => { this.deleteWorkspace(workspace.index); }
      },
      {
        label: 'Rename Workspace', icon: PrimeIcons.PENCIL,
        command: () => { this.editWorkspace(workspace, true); }
      },
      {
        label: 'Insert Workspace', icon: PrimeIcons.ARROW_UP,
        command: () => {
          this.insertWorkspace(workspace.index);
        }
      },
      {
        label: 'Move Left', icon: PrimeIcons.ARROW_LEFT,
        command: () => {
          this.moveWorkspaceIdx(workspace.index, workspace.index - 1);
        }
      },
      {
        label: 'Move Right', icon: PrimeIcons.ARROW_RIGHT,
        command: () => {
          this.moveWorkspaceIdx(workspace.index, workspace.index + 1);
        }
      },
      {
        label: 'Apply default workspace', icon: PrimeIcons.REFRESH,
        command: () => {
          this.applyDefaultWorkspace();
        }
      }
    ] as MenuItem[];
  }

  public editWorkspace(ws: DashboardWorkspaceIdxModel, calledFromContextMenu: boolean) {
    this.editFromContextMenu = calledFromContextMenu;
    this.selectedEditTab = ws;
  }

    private applyDefaultWorkspace(): void {
      this._dashboardService.cloneDashboard("00000000-0000-0000-0000-000000000000", this._organization).subscribe(() => {
        this._messenger.publish(new WorkspaceCloned());
      });
    }
}
